import React, { useEffect, useState } from "react";
import {
  AddedOn,
  CheckboxWrapper,
  EmailAddress,
  LastActive,
  More,
  Name,
  Position,
  Projects,
  Role,
  TableContainer,
  TableFilters,
  TableHead,
  TableHeadWrapper,
  Team,
} from "./styled";
import { CheckBox } from "../../../Inputs/CustomCheckbox/CheckBox";
import UsersRow from "../UsersRow";
import InfiniteScroll from "react-infinite-scroll-component";
import { SortedColumn } from "../../../../pages/UserManagment/pages/Users/data/mock";
import { useDispatch, useSelector } from "react-redux";
import { selectAllUsers } from "../../../../store/slices/userManagement";
import { getSelectedUsers } from "../../../../store/selectors/userManagement";
import { ThreeDots } from "react-bootstrap-icons";
import { handleColumnClick } from "../../utils/usersList";
import UsersTableMenu from "../../../UsersTableMenu";
import TableValuesFilter from "../../../TableValuesFilter";
import { applyFilters } from "../../../TableValuesFilter/utils/filters";
import { IUser } from "../../../../models/Users";
import { format } from "date-fns";
import { IFullUser, TeamMember } from "../../../../models/Teams";
import { getTeams } from "../../../../store/selectors/teams";
import { useParams } from "react-router-dom";
import { useAbilityContext } from "../../../../casl/context";
import { requestAllUsers } from "../../../../store/slices/users";
import { requestTeamUsersList } from "../../../../store/slices/teams";
import { getFullName } from "../../../../helpers/getInitials";

export interface ILastAction {
  key?: SortedColumn;
  ascending?: boolean;
  descending?: boolean;
  pined?: boolean;
}

export interface IMenuData {
  top: number;
  left: number;
  key: any;
}
export type IActiveFilters<T> = {
  [K in keyof T]?: {
    search?: string;
    filter?: string[];
    subKeyName?: keyof T[K];
    subKeyName2?: keyof T[K][keyof T[K]];
  };
};

type Props = {
  users: (IUser | TeamMember)[];
  usersCount: number;
  isTeamTable?: boolean;
};

const UsersList = ({ users, usersCount, isTeamTable }: Props) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const selectedUsers = useSelector(getSelectedUsers);
  const teams = useSelector(getTeams);
  const ability = useAbilityContext();
  const [maxData, setMaxData] = useState<number>(30);
  const [hasMore, setHasMore] = useState(true);
  const [lastAction, setLastAction] = useState<ILastAction>({});
  const [columnMenu, setColumnMenu] = useState<IMenuData | null>(null);
  const [isOpenFilter, setIsOpenFilter] = useState<SortedColumn | null>(null);
  const [usersList, setUsersList] = useState<(IUser | TeamMember)[]>(users);
  const [activeFilters, setActiveFilters] = useState<
    IActiveFilters<IUser | TeamMember>
  >({});

  const maxDataLength = usersCount;
  const end = maxData;
  const canUpdate = ability.can("update", "teamUsers");

  const fetchData = () => {
    if (maxData + 30 < maxDataLength) {
      setMaxData((prev) => prev + 30);
    } else {
      setMaxData(maxDataLength);
      setHasMore(false);
    }
  };

  const selectAll = () => {
    dispatch(
      selectAllUsers(
        usersList
          ?.slice(0, end)
          .map((item) => (isTeamTable ? (item as TeamMember).id : item?.uid))
      )
    );
  };

  useEffect(() => {
    if (isTeamTable) {
      dispatch(requestTeamUsersList({ id: id as string, limit: maxData }));
    } else {
      dispatch(requestAllUsers({ limit: maxData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxData, dispatch]);

  useEffect(() => {
    const newData = applyFilters({
      data: users,
      activeFilters,
    });

    setUsersList(newData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, users]);

  return (
    <>
      <TableContainer style={{ marginBottom: "20px" }}>
        <TableHeadWrapper>
          <TableHead>
            {canUpdate && (
              <CheckboxWrapper>
                <CheckBox
                  onChange={() => selectAll()}
                  checked={selectedUsers?.length === end}
                  status={
                    !!selectedUsers?.length && selectedUsers?.length < end
                      ? "indeterminate"
                      : selectedUsers?.length === end
                      ? "checked"
                      : "none"
                  }
                />
              </CheckboxWrapper>
            )}
            <Name>
              Name
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "name" }))
                }
              />
            </Name>
            <EmailAddress>
              Email address
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "email" }))
                }
              />
            </EmailAddress>
            {isTeamTable && (
              <Role>
                Role
                <ThreeDots
                  onClick={(event) =>
                    setColumnMenu(handleColumnClick({ event, key: "role" }))
                  }
                />
              </Role>
            )}
            <Position>
              Position
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "profile" }))
                }
              />
            </Position>
            {!isTeamTable && (
              <Team>
                Team
                <ThreeDots
                  onClick={(event) =>
                    setColumnMenu(handleColumnClick({ event, key: "teams" }))
                  }
                />
              </Team>
            )}
            <Projects>
              Projects
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "projects" }))
                }
              />
            </Projects>
            <AddedOn>
              Added on
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "role" }))
                }
              />
            </AddedOn>
            <LastActive>
              Last active
              <ThreeDots
                onClick={(event) =>
                  setColumnMenu(handleColumnClick({ event, key: "role" }))
                }
              />
            </LastActive>
            <More></More>
          </TableHead>
          <TableFilters>
            {canUpdate && <CheckboxWrapper $noPadding />}
            <Name $noPadding>
              <TableValuesFilter
                keyName={"name"}
                filter={false}
                dataList={users || []}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </Name>
            <EmailAddress $noPadding>
              <TableValuesFilter
                keyName={"email"}
                filter={false}
                dataList={users || []}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </EmailAddress>
            {isTeamTable && (
              <Role $noPadding>
                <TableValuesFilter
                  keyName={"role"}
                  disabled
                  filter={false}
                  dataList={users || []}
                  setIsOpenFilter={setIsOpenFilter}
                  isOpenFilter={isOpenFilter}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                />
              </Role>
            )}

            <Position $noPadding>
              <TableValuesFilter
                keyName={"profile" as any}
                subKeyName={"jobTitle" as any}
                dataList={users || []}
                filter={false}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </Position>
            {!isTeamTable && (
              <Team $noPadding>
                <TableValuesFilter
                  keyName={"teams" as any}
                  subKeyName={"teams" as any}
                  subKeyName2={"name" as any}
                  dataList={users || []}
                  setIsOpenFilter={setIsOpenFilter}
                  isOpenFilter={isOpenFilter}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                />
              </Team>
            )}
            <Projects $noPadding>
              <TableValuesFilter
                filter={false}
                keyName={"projects" as any}
                subKeyName={"count" as any}
                dataList={users || []}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </Projects>
            <AddedOn $noPadding>
              <TableValuesFilter
                filter={false}
                keyName={"profile" as any}
                subKeyName={"createdAt" as any}
                dataList={users || []}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </AddedOn>
            <LastActive $noPadding>
              <TableValuesFilter
                disabled
                keyName={"profile" as any}
                subKeyName={"lastLogin" as any}
                dataList={users || []}
                filter={false}
                setIsOpenFilter={setIsOpenFilter}
                isOpenFilter={isOpenFilter}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </LastActive>

            {canUpdate && <More $noPadding />}
          </TableFilters>
        </TableHeadWrapper>

        <InfiniteScroll
          dataLength={maxData}
          next={fetchData}
          hasMore={hasMore}
          loader={false && <h4>Loading...</h4>}
          endMessage={null}
        >
          <div
            style={{
              maxHeight: "100%",
              overflowY: "auto",
            }}
          >
            {usersList.map((item: IUser | TeamMember) => {
              const role = isTeamTable
                ? teams
                    .find((team) => team.id === id)
                    ?.members?.find((member) => member.email === item.email)
                    ?.role
                : undefined;

              return (
                <>
                  <UsersRow
                    uuid={
                      (item as TeamMember) && (item as TeamMember)?.user
                        ? (item as TeamMember)?.user?.uid
                        : item?.uid
                    }
                    user={
                      ((item as TeamMember) && (item as TeamMember)?.user
                        ? (item as TeamMember).user
                        : item) as IFullUser
                    }
                    teamMemberId={
                      (item as TeamMember) ? (item as TeamMember)?.id : null
                    }
                    name={
                      getFullName(
                        (item as TeamMember)?.user?.profile?.firstName,
                        (item as TeamMember)?.user?.profile?.lastName
                      ) ||
                      (item as TeamMember)?.user?.name ||
                      item?.name
                    }
                    email={item.email}
                    team={
                      isTeamTable
                        ? undefined
                        : "teams" in item
                        ? item.teams?.teams?.map(
                            (team: { name: string }) => team.name
                          )
                        : [""]
                    }
                    projects={
                      isTeamTable
                        ? 0
                        : "projects" in item
                        ? item.projects?.count
                        : 0
                    }
                    addedOn={
                      isTeamTable
                        ? (item as TeamMember)?.user?.profile?.createdAt
                          ? format(
                              new Date(
                                (item as TeamMember)?.user?.profile?.createdAt!
                              ),
                              "MM.dd.yyyy HH:mm"
                            )
                          : "-"
                        : (item as IUser)?.profile?.createdAt
                        ? format(
                            new Date((item as IUser)?.profile?.createdAt!),
                            "MM.dd.yyyy HH:mm"
                          )
                        : "-"
                    }
                    lastActive={format(new Date(), "MM.dd.yyyy HH:mm")}
                    role={role}
                    avatar={(item as TeamMember)?.user?.profile?.avatar}
                    position={
                      isTeamTable
                        ? (item as TeamMember)?.user?.profile?.jobTitle
                          ? (item as TeamMember)?.user?.profile?.jobTitle
                          : "-"
                        : (item as IUser)?.profile?.jobTitle
                        ? (item as IUser)?.profile?.jobTitle
                        : "-"
                    }
                    teamTable={isTeamTable}
                  />
                </>
              );
            })}
          </div>
        </InfiniteScroll>
      </TableContainer>
      {columnMenu && (
        <UsersTableMenu
          lastAction={lastAction}
          setLastAction={setLastAction}
          columnMenu={columnMenu}
          setColumnMenu={setColumnMenu}
          usersData={usersList}
          setUsersList={setUsersList}
        />
      )}
    </>
  );
};

export default UsersList;
